import { useEffect, useState } from "react";
import {
  CircleNotch,
  CheckCircle,
  FloppyDisk,
  Buildings,
  EnvelopeSimple,
  Phone,
  WhatsappLogo,
  Globe,
  Lock,
  Eye,
  EyeSlash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const SECTIONS = [
  {
    title: "Company",
    icon: Buildings,
    fields: [
      { key: "company_name", label: "Company name", placeholder: "Auromine Retail POS Private Limited" },
      { key: "company_address", label: "Registered address", placeholder: "Chennai, Tamil Nadu, India" },
      { key: "company_gstin", label: "GSTIN (optional)", placeholder: "33AAAAA0000A1Z5" },
    ],
  },
  {
    title: "Email addresses",
    icon: EnvelopeSimple,
    fields: [
      { key: "general_email", label: "General · hello@", placeholder: "hello@mineai.io", type: "email" },
      { key: "support_email", label: "Support · support@", placeholder: "support@mineai.io", type: "email" },
      { key: "billing_email", label: "Billing · billing@", placeholder: "billing@mineai.io", type: "email" },
      { key: "legal_email", label: "Legal · legal@", placeholder: "legal@mineai.io", type: "email" },
      { key: "privacy_email", label: "Privacy · privacy@", placeholder: "privacy@mineai.io", type: "email" },
    ],
  },
  {
    title: "Phone & WhatsApp",
    icon: WhatsappLogo,
    fields: [
      { key: "support_phone", label: "Support phone", placeholder: "+91 98xxxxxxxx" },
      { key: "whatsapp_number", label: "WhatsApp number (with country code)", placeholder: "+91 98xxxxxxxx" },
      { key: "whatsapp_default_message", label: "WhatsApp default message", placeholder: "Hi Mine AI team..." },
    ],
  },
  {
    title: "Social",
    icon: Globe,
    fields: [
      { key: "instagram_url", label: "Instagram URL", placeholder: "https://instagram.com/mineai.io" },
      { key: "twitter_url", label: "Twitter / X URL", placeholder: "https://x.com/mineai" },
    ],
  },
  {
    title: "AI keys (sensitive)",
    icon: Lock,
    sensitive: true,
    fields: [
      { key: "openrouter_api_key", label: "OpenRouter API key (sk-or-v1-...)", placeholder: "Paste your OpenRouter key — leave blank to keep existing", sensitive: true },
    ],
  },
];

export default function AdminSettings() {
  const { refetch: refetchPublicSettings } = useSettings();
  const [draft, setDraft] = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/settings");
      setDraft(r.data.settings);
      setOriginal(r.data.settings);
    } catch {
      toast.error("Could not load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const dirty =
    draft &&
    original &&
    SECTIONS.flatMap((s) => s.fields).some((f) => (draft[f.key] || "") !== (original[f.key] || ""));

  const save = async () => {
    if (!draft.company_name || !draft.company_address || !draft.general_email || !draft.support_email) {
      toast.error("Company name, address, hello@ and support@ are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        company_name: draft.company_name,
        company_address: draft.company_address,
        company_gstin: draft.company_gstin || "",
        general_email: draft.general_email,
        support_email: draft.support_email,
        billing_email: draft.billing_email || null,
        legal_email: draft.legal_email || null,
        privacy_email: draft.privacy_email || null,
        support_phone: draft.support_phone || "",
        whatsapp_number: draft.whatsapp_number || "",
        whatsapp_default_message: draft.whatsapp_default_message || "",
        instagram_url: draft.instagram_url || "",
        twitter_url: draft.twitter_url || "",
      };
      const r = await api.put("/admin/settings", payload);
      setOriginal(r.data.settings);
      setDraft(r.data.settings);
      // Propagate changes to public footer + WhatsApp button without a hard reload
      refetchPublicSettings();
      toast.success("Settings saved — they'll appear across the app immediately.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold">
            Settings
          </p>
          <h1 className="font-display text-3xl sm:text-4xl mt-2 text-[#0A1128]">
            Contact & company details
          </h1>
          <p className="mt-2 text-sm text-[#4A5568] max-w-2xl">
            These details appear in the footer, the WhatsApp button, and on policy pages. Update once — they propagate everywhere.
          </p>
        </div>
        <button
          data-testid="settings-save-btn"
          onClick={save}
          disabled={!dirty || saving}
          className="px-4 py-2.5 rounded-md bg-[#3B5BDB] text-white font-medium hover:bg-[#2F49AF] smooth disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {saving ? (
            <CircleNotch size={16} className="animate-spin" weight="bold" />
          ) : dirty ? (
            <FloppyDisk size={16} weight="duotone" />
          ) : (
            <CheckCircle size={16} weight="duotone" />
          )}
          {saving ? "Saving..." : dirty ? "Save changes" : "Saved"}
        </button>
      </div>

      {loading || !draft ? (
        <div className="mt-10 flex items-center gap-3 text-[#4A5568]">
          <CircleNotch className="animate-spin" weight="bold" /> Loading settings...
        </div>
      ) : (
        <div className="mt-8 space-y-5">
          {SECTIONS.map((s) => (
            <div key={s.title} className="mc-card p-6" data-testid={`section-${s.title.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="flex items-center gap-2">
                <s.icon size={20} weight="duotone" className="text-[#3B5BDB]" />
                <h2 className="font-display text-xl text-[#0A1128]">{s.title}</h2>
              </div>
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                {s.fields.map((f) => (
                  <div key={f.key} className={(f.key === "company_address" || f.key === "whatsapp_default_message" || f.sensitive) ? "sm:col-span-2" : ""}>
                    <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">
                      {f.label}
                    </label>
                    <input
                      type={f.sensitive ? "password" : (f.type || "text")}
                      autoComplete={f.sensitive ? "new-password" : undefined}
                      data-testid={`input-${f.key}`}
                      value={draft[f.key] || ""}
                      onChange={(e) => update(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] font-mono"
                    />
                    {f.sensitive && (
                      <p className="mt-1.5 text-[11px] text-[#4A5568]/80">
                        Stored encrypted-at-rest in MongoDB. Leave blank to keep the existing key. Get yours at{" "}
                        <a href="https://openrouter.ai/settings/keys" target="_blank" rel="noopener noreferrer" className="text-[#3B5BDB] hover:underline">
                          openrouter.ai/settings/keys
                        </a>.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <PasswordChangeCard />
        </div>
      )}
    </div>
  );
}

function PasswordChangeCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmNext, setConfirmNext] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirmNext) {
      toast.error("New password and confirmation don't match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated. Use the new password next time you sign in.");
      setCurrent("");
      setNext("");
      setConfirmNext("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="mc-card p-6"
      data-testid="section-password"
    >
      <div className="flex items-center gap-2">
        <Lock size={20} weight="duotone" className="text-[#3B5BDB]" />
        <h2 className="font-display text-xl text-[#0A1128]">Change password</h2>
      </div>
      <p className="mt-1 text-xs text-[#4A5568]">
        Pick a strong, unique password — at least 8 characters. We never email passwords.
      </p>
      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2 relative">
          <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">
            Current password
          </label>
          <input
            type={show ? "text" : "password"}
            data-testid="input-current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
            className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
          />
          <button
            type="button"
            onClick={() => setShow(!show)}
            className="absolute right-2 top-7 p-2 text-[#4A5568] hover:text-[#0A1128]"
            aria-label={show ? "Hide passwords" : "Show passwords"}
            data-testid="toggle-password-visibility"
          >
            {show ? <EyeSlash size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">
            New password
          </label>
          <input
            type={show ? "text" : "password"}
            data-testid="input-new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
            className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">
            Confirm new password
          </label>
          <input
            type={show ? "text" : "password"}
            data-testid="input-confirm-password"
            value={confirmNext}
            onChange={(e) => setConfirmNext(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
            className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
          />
        </div>
      </div>
      <div className="mt-5">
        <button
          type="submit"
          data-testid="change-password-btn"
          disabled={saving || !current || !next || !confirmNext}
          className="px-4 py-2.5 rounded-md bg-[#3B5BDB] text-white font-medium hover:bg-[#2F49AF] smooth disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {saving ? <CircleNotch size={16} className="animate-spin" weight="bold" /> : <Lock size={16} weight="duotone" />}
          {saving ? "Updating..." : "Update password"}
        </button>
      </div>
    </form>
  );
}
